import React from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  DatePicker as MuiDatePicker,
  DateTimePicker as MuiDateTimePicker,
  TimePicker as MuiTimePicker,
} from "@mui/x-date-pickers";

type DateConstraint = "past" | "future" | "not-past" | "not-future";

export interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  onClose?: () => void;
  hasError?: boolean;
  timePicker?: boolean;
  combinedInput?: boolean;
  testId?: string;
  dateConstraint?: DateConstraint;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  slotProps?: any;
}

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const isSameOrAfter = (a: Date, b: Date) => a.getTime() >= b.getTime();
const isSameOrBefore = (a: Date, b: Date) => a.getTime() <= b.getTime();

const normalizeDay = (day: Date) => {
  const d = new Date(day);
  d.setHours(0, 0, 0, 0);
  return d;
};

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  onClose,
  hasError,
  timePicker = true,
  combinedInput = false,
  testId,
  dateConstraint,
  minDate,
  maxDate,
  disabled = false,
  slotProps,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const today = startOfToday();

  // Map constraint to MUI props
  let disablePast = false;
  let disableFuture = false;
  let shouldDisableDate: ((day: Date) => boolean) | undefined;

  switch (dateConstraint) {
    case "not-past":
      disablePast = true;
      break;
    case "not-future":
      disableFuture = true;
      break;
    case "past":
      shouldDisableDate = (day) => isSameOrAfter(normalizeDay(day), today);
      break;
    case "future":
      shouldDisableDate = (day) => isSameOrBefore(normalizeDay(day), today);
      break;
    default:
      break;
  }

  const handleDateChange = (newDate: Date | null) => {
    if (newDate) {
      const current = value ?? new Date();
      newDate.setHours(current.getHours());
      newDate.setMinutes(current.getMinutes());
    }
    onChange(newDate);
  };

  const handleTimeChange = (newTime: Date | null) => {
    if (newTime) {
      const base = value ?? new Date();
      const updated = new Date(base);
      updated.setHours(newTime.getHours());
      updated.setMinutes(newTime.getMinutes());
      onChange(updated);
    }
  };

  if (combinedInput && timePicker) {
    return (
      <MuiDateTimePicker
        value={value}
        onChange={onChange}
        onClose={onClose}
        ampm={false}
        format="dd/MM/yyyy HH:mm"
        slots={{ openPickerIcon: CalendarMonthIcon }}
        shouldDisableDate={shouldDisableDate}
        disablePast={disablePast}
        disableFuture={disableFuture}
        minDateTime={minDate}
        maxDateTime={maxDate}
        disabled={disabled}
        slotProps={{
          textField: {
            fullWidth: true,
            error: hasError,
            helperText: "",
            size: "small",
            placeholder: "DD/MM/YYYY  HH:MM",
            inputProps: { "data-testid": testId },
            sx: {
              "& .MuiOutlinedInput-root": { height: "40px" },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: hasError ? "#E1172F" : "rgb(187, 193, 197)",
              },
            },
          },
          // @ts-expect-error data-testid
          openPickerButton: { "data-testid": `${testId}-open-picker` },
          field: { clearable: true },
          popper: {
            sx: { zIndex: 1700 },
          },
        }}
        sx={{ width: "100%" }}
      />
    );
  }

  if (combinedInput) {
    return (
      <MuiDatePicker
        value={value}
        onChange={handleDateChange}
        onClose={onClose}
        slots={{ openPickerIcon: CalendarMonthIcon }}
        shouldDisableDate={shouldDisableDate}
        disablePast={disablePast}
        disableFuture={disableFuture}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        slotProps={{
          textField: {
            fullWidth: true,
            error: hasError,
            helperText: "",
            size: "small",
            inputProps: { "data-testid": testId },
            sx: {
              "& .MuiOutlinedInput-root": { height: "40px" },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: hasError ? "#E1172F" : "rgb(187, 193, 197)",
              },
            },
          },
          field: { clearable: true, onClear: () => handleDateChange(null) } as any,
          openPickerButton: { "data-testid": `${testId}-open-picker` },
          popper: {
            sx: { zIndex: 1700 },
          },
          ...slotProps,
        }}
        sx={{ width: "100%" }}
      />
    );
  }

  return (
    <Box
      display="flex"
      flexDirection={isSmallScreen ? "column" : "row"}
      gap={2}
      alignItems="flex-start"
      width="100%"
    >
      <MuiDatePicker
        value={value}
        onChange={handleDateChange}
        onClose={onClose}
        slots={{ openPickerIcon: CalendarMonthIcon }}
        shouldDisableDate={shouldDisableDate}
        disablePast={disablePast}
        disableFuture={disableFuture}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        slotProps={{
          textField: {
            fullWidth: true,
            error: hasError,
            helperText: "",
            inputProps: { "data-testid": testId },
            sx: {
              "& .MuiPickersOutlinedInput-notchedOutline": {
                borderColor: hasError ? "#E1172F" : "rgb(187, 193, 197)",
              },
            },
            InputProps: { sx: { height: "40px" } },
          },
          field: { clearable: true, onClear: () => handleDateChange(null) } as any,
          openPickerButton: { "data-testid": `${testId}-open-picker` },
          popper: {
            sx: { zIndex: 1700 },
          },
          ...slotProps,
        }}
      />

      {timePicker && (
        <MuiTimePicker
          value={value}
          minTime={minDate}
          maxTime={maxDate}
          disableIgnoringDatePartForTimeValidation
          disabled={disabled}
          onChange={handleTimeChange}
          slotProps={{
            textField: {
              fullWidth: true,
              helperText: "",
              InputProps: { sx: { height: "40px" } },
              sx: {
                "& .MuiPickersOutlinedInput-notchedOutline": {
                  borderColor: hasError ? "#E1172F" : "rgb(187, 193, 197)",
                },
              },
            },
          }}
          sx={{ width: isSmallScreen ? "100%" : "200px" }}
        />
      )}
    </Box>
  );
};

export default DatePicker;
